import { isServer } from "solid-js/web";

import { LocalStorageKey } from "@/constants/constants";
import { ENV } from "@/enum/system";
import { useUrlStorage } from "@/hooks/useUrlStorage";

import { getLocalStorageItem, setLocalStorageItem } from "../localStorage";
import { BaseSDK, SDKConfig } from "./base";
import { sdkManager } from "./sdkManager";
import { SDKName } from "./types";

interface ThinkingDataInitConfig {
  appId: string;
  showLog?: boolean;
  serverUrl?: string;
  autoTrack?: {
    pageShow?: boolean;
    pageHide?: boolean;
  };
}

interface PendingEvent {
  event: string;
  data?: Record<string, unknown>;
}

declare global {
  interface Window {
    ta: {
      init: (config: ThinkingDataInitConfig) => void;
      setDistinctId: (id: string) => void;
      login: (id: string) => void;
      logout: () => void;
      track: (eventName: string, data?: Record<string, unknown>) => void;
      timeEvent: (eventName: string) => void;
      setSuperProperties: (properties: Record<string, unknown>) => void;
      setDynamicSuperProperties: (
        callback: () => Record<string, unknown>,
      ) => void;
      clearSuperProperties: () => void;
      trackFirst: (params: { eventName: string }) => void;
    };
  }
}

export class ThinkingDataSDK extends BaseSDK {
  private isInitialized = false;
  private eventQueue: PendingEvent[] = [];

  constructor(config: SDKConfig) {
    super(SDKName.ThinkingDataSDK, config);
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    try {
      // Use a dynamic import with type assertion since the module doesn't have type declarations
      const thinkingdata = (await import("thinkingdata-browser")).default;
      const config: ThinkingDataInitConfig = {
        appId: String(this.config.appId),
        showLog: this.config.showLog as boolean,
        serverUrl: this.config.serverUrl as string,
        autoTrack: {
          pageShow: true,
          pageHide: true,
        },
      };

      window.ta = thinkingdata;
      thinkingdata.init(config);

      if (this.config.deviceId) {
        thinkingdata.setDistinctId(this.config.deviceId);
      }
      sdkManager.updateThinkingDataProperties();
      const firstInit = getLocalStorageItem(
        LocalStorageKey.FIRST_THINKINGDATA,
        0,
      );
      if (firstInit == 0) {
        setLocalStorageItem(LocalStorageKey.FIRST_THINKINGDATA, 1);
        thinkingdata.trackFirst({ eventName: "ta_new_device" });
      }

      this.isInitialized = true;
      // 初始化完成后，处理队列中的事件
      this.flushEventQueue();
    } catch (error) {
      console.error("Failed to initialize ThinkingData:", error);
    } finally {
      this.tryReportThinkingData();
    }
  }

  /** 处理队列中所有待上报的事件 */
  private flushEventQueue(): void {
    if (!this.isInitialized || !window.ta || !window.ta.track) {
      return;
    }

    while (this.eventQueue.length > 0) {
      const pendingEvent = this.eventQueue.shift();
      if (pendingEvent) {
        try {
          window.ta.track(pendingEvent.event, pendingEvent.data);
        } catch (error) {
          console.error(
            `Failed to report queued event ${pendingEvent.event}:`,
            error,
          );
        }
      }
    }
  }

  private tryReportThinkingData(): void {
    const { getStoredParams } = useUrlStorage();
    const urlObj = new URL(getStoredParams()?.url || window.location.href);
    const addParams = ["adtp", "adid", "adex"];
    let needReport = false;
    const eventName = "page_view_with_params_from_crm";
    if (addParams.some((param) => urlObj.searchParams.get(param))) {
      needReport = true;
    }

    if (needReport) {
      sdkManager.tdReportEvent(eventName, {
        adid: urlObj.searchParams.get("adid"),
        adex: urlObj.searchParams.get("adex"),
        adtp: urlObj.searchParams.get("adtp"),
        url: urlObj.toString(),
      });
    }
  }

  public login(userId: string): void {
    if (!window.ta) return;
    window.ta.login(userId);
  }

  public logout(): void {
    if (!window.ta) return;
    window.ta.logout();
  }

  public setSuperProperties(properties: Record<string, unknown>): void {
    if (!window.ta) return;
    window.ta.setSuperProperties(properties);
  }

  public setDynamicSuperProperties(
    callback: () => Record<string, unknown>,
  ): void {
    if (!window.ta) return;
    window.ta.setDynamicSuperProperties(callback);
  }

  public clearSuperProperties(): void {
    if (!window.ta) return;
    window.ta.clearSuperProperties();
  }

  public trackFirst(eventName: string): void {
    if (!window.ta) return;
    window.ta.trackFirst({ eventName });
  }

  public reportEvent(event: string, data?: Record<string, unknown>) {
    // 如果还未初始化，将事件加入队列
    if (!this.isInitialized) {
      this.eventQueue.push({ event, data });
      return;
    }

    // 如果已初始化但 ta 对象不存在，也加入队列等待
    if (!window.ta || !window.ta.track) {
      this.eventQueue.push({ event, data });
      return;
    }

    // 正常上报
    try {
      return window.ta.track(event, data);
    } catch (error) {
      console.error(`Failed to report event ${event}:`, error);
    }
  }

  public timeEvent(eventName: string): void {
    if (!window.ta) return;
    window.ta.timeEvent(eventName);
  }
}
